// src/App.jsx - Profound Philosophy Main Application

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import { AnalyticsConsentNotice } from '@ddd/analytics';
import { AppProvider, useApp } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { PremiumProvider } from './context/PremiumContext';
import { initializeGamification } from './stores/gamificationStore';

// Layout
import Layout from './components/layout/Layout';

// Pages
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import AssessmentPage from './pages/AssessmentPage';
import ModulesPage from './pages/ModulesPage';
import ModuleDetailPage from './pages/ModuleDetailPage';
import ToolkitPage from './pages/ToolkitPage';
import CompanionPage from './pages/CompanionPage';
import JournalPage from './pages/JournalPage';
import PositionsPage from './pages/PositionsPage';
import LineagePage from './pages/LineagePage';
import ResourcesPage from './pages/ResourcesPage';
import ProfilePage from './pages/ProfilePage';
import DataPrivacyPage from './pages/DataPrivacyPage';
import LegalRedirect from './components/shared/LegalRedirect';
import { DDD_TERMS_URL } from './config/legalLinks';
import SupportPage from './pages/SupportPage';
import TokenExchangePage from './pages/TokenExchangePage';

// Native shell
import NativeDeepLinkHandler from './components/shared/NativeDeepLinkHandler';
import Paywall from './components/premium/Paywall';
// Removed local auth pages - using centralised Auth Hub

// Styles
import './styles/globals.css';

// First-visit analytics consent notice. onDecision persists the choice into
// profoundPhilosophy_preferences so the boot path and the privacy-page toggle
// stay in agreement with the rail's own stored consent — without it, a banner
// "yes" would silently revert on the next visit.
const ConsentNotice = () => {
  const { updatePreferences } = useApp();
  return (
    <AnalyticsConsentNotice
      appName="Profound Philosophy"
      privacyHref="/privacy"
      accent="#10b981"
      onDecision={(granted) => updatePreferences({ analytics: granted })}
    />
  );
};

function App() {
  // Initialize gamification system on app load
  useEffect(() => {
    initializeGamification();
  }, []);

  return (
    <AuthProvider>
      <PremiumProvider>
      <AppProvider>
        {/* reducedMotion="user" honours the OS accessibility setting app-wide */}
        <MotionConfig reducedMotion="user">
        <Router>
            {/* Must sit inside the Router: it navigates the auth-hub callback
                into /auth/token-exchange. No-op on the web build. */}
            <NativeDeepLinkHandler />
            {/* Opened by the `pph:open-paywall` event any gate dispatches. */}
            <Paywall />
            <Routes>
              {/* Public Routes */}
              <Route element={<Layout />}>
                {/* The iOS app opens on the Dashboard, not the website's marketing
                    home page (Pemberton's approved pattern). */}
                <Route path="/" element={Capacitor.isNativePlatform() ? <Navigate to="/dashboard" replace /> : <HomePage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/assessment" element={<AssessmentPage />} />
                <Route path="/modules" element={<ModulesPage />} />
                <Route path="/modules/:moduleId" element={<ModuleDetailPage />} />
                <Route path="/toolkit" element={<ToolkitPage />} />
                <Route path="/toolkit/:toolId" element={<ToolkitPage />} />
                {/* Sofia handles her own sign-in gate: the page explains WHY
                    she needs an account before sending anyone to auth. */}
                <Route path="/companion" element={<CompanionPage />} />
                <Route path="/journal" element={<JournalPage />} />
                <Route path="/positions" element={<PositionsPage />} />
                <Route path="/lineage" element={<LineagePage />} />
                {/* A thinker's profile is a URL of its own, so module handoffs
                    ("Meet Rawls") and shared links land on the person directly */}
                <Route path="/lineage/:thinkerId" element={<LineagePage />} />
                <Route path="/resources" element={<ResourcesPage />} />
                <Route path="/privacy" element={<DataPrivacyPage />} />
                {/* Terms are DDD-wide (src/config/legalLinks.js). */}
                <Route path="/terms" element={<LegalRedirect to={DDD_TERMS_URL} label="Dive Deep Dialogues Terms of Use" />} />
                {/* Also the Support URL in App Store Connect; reviewers open it. */}
                <Route path="/support" element={<SupportPage />} />
                {/* Profile handles both states itself: on iOS it is a tab, and a tab
                    should show a sign-in card, not bounce you to a web page. */}
                <Route path="/profile" element={<ProfilePage />} />
              </Route>

              {/* Auth Hub SSO callback, outside Layout so no nav renders
                  while the session is being established */}
              <Route path="/auth/token-exchange" element={<TokenExchangePage />} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            {/* First-visit analytics consent notice */}
            <ConsentNotice />
        </Router>
        </MotionConfig>
      </AppProvider>
      </PremiumProvider>
    </AuthProvider>
  );
}

export default App;